import { getField, updateField } from 'vuex-map-fields'
import { getEmptyDeal, getEmptyDealCandidate } from './models/dealV2'
import updateStatus from '@/store/helpers/updateStatus'
import { IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_DEALS, PATH_SUBMIT_DEAL, PATH_APPROVE_DEAL, PATH_REJECT_DEAL } from '@/lib/constants/api'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'
import { notifySuccess, notifyError } from '@/lib/notifications'
import api from '@/lib/api'

const dealV2 = {
  namespaced: true,
  state: {
    deal: getEmptyDeal(),
    dealList: []
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    addCandidate: (state) => {
      state.deal.candidates.push(getEmptyDealCandidate())
    },
    deleteCandidate: (state, dealCandidateId) => {
      state.deal.candidates = state.deal.candidates.filter(
        candidate => candidate.id !== dealCandidateId
      )
    },
    clearDeal: (state) => {
      state.deal = getEmptyDeal()
    },
    setDeal: (state, deal) => {
      state.deal = deal
    },
    setDeals: (state, deals) => {
      state.dealList = deals
    }
  },
  actions: {
    clearDeal ({ commit }) {
      commit('clearDeal')
    },
    addCandidate: ({ commit }) => {
      commit('addCandidate')
    },
    deleteCandidate: async ({ commit, state, dispatch }, dealCandidateId) => {
      if (dealCandidateId < 0) {
        // Frontend-only candidate (negative ID) — remove from local array without hitting the backend
        commit('deleteCandidate', dealCandidateId)
        notifySuccess(dispatch, 'Candidate deleted')
        return
      }

      // Persisted candidate — send DELETE request then remove locally
      const candidate = state.deal.candidates.find(c => c.id === dealCandidateId)
      const dealAssignmentId = candidate && candidate.dealAssignment ? candidate.dealAssignment.id : null

      showProgressModal({ dispatch, message: 'Deleting candidate, please wait...' })
      return api.doDelete({ path: `${PATH_DEALS}/candidate/${dealCandidateId}/${dealAssignmentId}` })
        .then(() => {
          commit('deleteCandidate', dealCandidateId)
          notifySuccess(dispatch, 'Candidate deleted')
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to delete candidate. Try again later')
        }).finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getDeal ({ commit, dispatch }, dealId) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting contract, please wait...' })

      return api.doGet({ path: `${PATH_DEALS}/${dealId}` })
        .then(response => response.data)
        .then(deal => {
          commit('updateStatus', { status: COMPLETE })
          commit('setDeal', deal)
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getDeals: ({ commit, dispatch }, filters = {}) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting deals, please wait...' })
      return api.doGet({ path: PATH_DEALS, parameters: filters })
        .then(json => json.data)
        .then(deals => {
          commit('updateStatus', { status: COMPLETE })
          commit('setDeals', deals)
          return deals
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to select deals')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getFieldsWithFilters: ({ dispatch, rootGetters }) => {
      return dispatch('getDeals', { ...rootGetters['dealFilters/getSearchObject'] })
    },
    _handleDealRequest ({ state, commit, dispatch }, { path, action, actionMessage, successMessage }) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: actionMessage })

      return api.doPost({ path, data: state.deal })
        .then(deal => {
          commit('updateStatus', { status: COMPLETE })

          if (!deal.data) {
            throw new Error(`Unable to ${action} deal. Check required fields.`)
          }

          if (!deal.data.validationErrors) {
            notifySuccess(dispatch, successMessage)
          }
          commit('setDeal', deal.data)
          return deal
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, `Unable to ${action} deal. Try again later`)
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveOrUpdateDeal ({ dispatch }) {
      return dispatch('_handleDealRequest', {
        path: PATH_DEALS,
        action: 'save',
        actionMessage: 'Saving deal, please wait...',
        successMessage: 'Deal saved'
      })
    },
    submitDeal ({ dispatch }) {
      return dispatch('_handleDealRequest', {
        path: PATH_SUBMIT_DEAL,
        action: 'submit',
        actionMessage: 'Submitting deal, please wait...',
        successMessage: 'Deal submitted'
      })
    },
    approveDeal ({ dispatch }) {
      return dispatch('_handleDealRequest', {
        path: PATH_APPROVE_DEAL,
        action: 'approve',
        actionMessage: 'Approving deal, please wait...',
        successMessage: 'Deal approved'
      })
    },
    rejectDeal ({ dispatch }) {
      return dispatch('_handleDealRequest', {
        path: PATH_REJECT_DEAL,
        action: 'reject',
        actionMessage: 'Rejecting deal, please wait...',
        successMessage: 'Deal rejected'
      })
    },
    deleteDeal ({ commit, dispatch }, id = null) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Deleting deal, please wait...' })
      return api.doDelete({
        path: `${PATH_DEALS}/${id}`
      })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to delete deal')
          }

          commit('clearDeal')
          notifySuccess(dispatch, 'Deal deleted')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to delete deal')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    }
  }
}

export default dealV2
